import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import UIButton from 'Components/UIComponents/UIButton';
import dayjs from 'dayjs';
import { useShowPlanUpgradeSuccessPageChange } from 'Hooks/Auth';
import { discountPlanPrices, PlanDurations } from 'Interfaces/Billing';
import { AuthorizedRoutePaths, settingsRedirectionMap } from 'Interfaces/RoutePaths';
import SuccessPageWrapper from 'Layouts/SuccessPageWrapper';
import { formatDateToStringForTrialInfo } from 'Utils/formatters';
import { selectUserPlan } from 'Utils/selectors';

interface ChangePlanSuccessProps {
  successRequired?: boolean;
}

const changePlanSuccessPathRegEx = /\/((business|personal)-start|welcome-(business|personal))$/;

function ChangePlanSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ChangePlanSuccessProps;

  const [changeShowPlanUpgradeSuccessPage] = useShowPlanUpgradeSuccessPageChange();
  const plan = useSelector(selectUserPlan);

  if (!locationState.successRequired) navigate(AuthorizedRoutePaths.BASE_PATH);

  const price = discountPlanPrices[plan.type][plan.duration];
  const nextBillingDate = dayjs(new Date())
    .add(plan.duration === PlanDurations.MONTHLY ? 1 : 12, 'month')
    .format('MMMM D, YYYY');

  const handleContinue = () => {
    navigate(settingsRedirectionMap[AuthorizedRoutePaths.SETTINGS_BILLING], {
      replace: true,
    });
  };

  useEffect(() => {
    changeShowPlanUpgradeSuccessPage(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!changePlanSuccessPathRegEx.test(location.pathname)) {
      navigate(AuthorizedRoutePaths.BASE_PATH);
    }
  }, [location.pathname, navigate]);

  return (
    <SuccessPageWrapper>
      <div className="successPages__inner">
        <div className="successPages__wrapper">
          <p className="successPages__header">Thank you for signing up! 🎉</p>
          <p className="successPages__text">
            We&apos;re so excited to have you with us. Your subscription starts today and
            will automatically renew on{' '}
            <b>{formatDateToStringForTrialInfo(nextBillingDate)}</b> at{' '}
            <b>${price}/month</b>.
          </p>
          <p className="successPages__text">
            Want to make changes? You’re always in control through your account settings.
          </p>
          <UIButton
            priority="primary"
            title={'Continue'}
            handleClick={handleContinue}
            className="successPages__button--download"
          />
        </div>
      </div>
    </SuccessPageWrapper>
  );
}

export default ChangePlanSuccess;
